/**
 * React Query hook for form dropdown options.
 * Replaces useState/useEffect + SDK with built-in caching.
 */
import { useQuery } from '@tanstack/react-query';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';
import { SelectOption, DropdownItem, GenericDropdownData } from '../types/formTypes';

function parseDropdownResponse(response: unknown): GenericDropdownData {
    const dropdownData: GenericDropdownData = {};

    if (Array.isArray(response)) {
        response.forEach((item: DropdownItem) => {
            if (item.dropDownName && item.data) {
                dropdownData[item.dropDownName] = item.data;
            }
        });
    } else if (response && typeof response === 'object') {
        Object.assign(dropdownData, response);
    }

    return dropdownData;
}

export const useFormOptions = () => {
    const getToken = useAccessToken();

    const query = useQuery<GenericDropdownData>({
        queryKey: ['microserviceGenerator', 'dropdownData'],
        queryFn: async () => {
            const response = await fetchApi('/api/microservicegenerator/dropdown-data', getToken);
            return parseDropdownResponse(response);
        },
        staleTime: 10 * 60 * 1000, // 10 minutes — dropdown data rarely changes
        retry: 2,
    });

    const data = query.data ?? {};

    return {
        orgNameOptions: (data['organization'] ?? []) as SelectOption[],
        techStackOptions: (data['techStack'] ?? []) as SelectOption[],
        databaseOptions: (data['database'] ?? []) as SelectOption[],
        domainOptions: (data['domain'] ?? []) as SelectOption[],
        functionalAreaOptions: (data['functionalArea'] ?? []) as SelectOption[],
        isLoading: query.isLoading,
        error: query.error ? (query.error as Error).message : null,
    };
};
